import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import employeeAuthService from '../services/EmployeeAuthService';
import ErrorHandler from '../utils/ErrorHandler';

/**
 * Employee Authentication Store
 * Manages employee authentication state, login/logout, and session persistence
 */
export const useEmployeeAuthStore = create(
  persist(
    (set, get) => ({
      // State
      isAuthenticated: false,
      employee: null,
      isLoading: false,
      error: null,
      isInitialized: false,

      // Actions
      login: async (credentials) => {
        set({ isLoading: true, error: null });
        
        try {
          const result = await employeeAuthService.login(credentials);
          
          if (result.success) {
            set({
              isAuthenticated: true,
              employee: result.employee,
              isLoading: false,
              error: null
            });
            return result;
          } else {
            throw new Error(result.message || 'Login failed');
          }
        } catch (error) {
          const errorInfo = ErrorHandler.handleApiError(error, 'Employee Login');
          set({
            isAuthenticated: false,
            employee: null,
            isLoading: false,
            error: errorInfo.message
          });
          throw error;
        }
      },

      logout: () => {
        employeeAuthService.logout();
        set({
          isAuthenticated: false,
          employee: null,
          error: null,
          isInitialized: false // Reset initialization flag to allow re-login
        });
      },

      // Handle 401 errors - force logout and clear all data
      handle401Error: () => {
        console.log('[EmployeeAuthStore] Handling 401 error - forcing logout');
        employeeAuthService.logout();
        set({
          isAuthenticated: false,
          employee: null,
          error: 'Your session has expired. Please log in again.',
          isInitialized: true,
          isLoading: false
        });
        
        // Force a page reload to ensure clean state
        setTimeout(() => {
          if (typeof window !== 'undefined') {
            window.location.reload();
          }
        }, 1000);
      },

      initialize: async () => {
        if (get().isInitialized) return get().isAuthenticated;
        
        set({ isLoading: true });
        
        try {
          const isLoggedIn = await employeeAuthService.initialize();
          const employee = employeeAuthService.getCurrentEmployee();
          
          set({
            isAuthenticated: isLoggedIn,
            employee: employee,
            isLoading: false,
            isInitialized: true,
            error: null
          });
          
          return isLoggedIn;
        } catch (error) {
          console.error('Employee auth initialization error:', error);
          set({
            isAuthenticated: false,
            employee: null,
            isLoading: false,
            isInitialized: true,
            error: null
          });
          return false;
        }
      },

      clearError: () => set({ error: null }),

      // Session validation
      validateSession: async () => {
        try {
          const isValid = await employeeAuthService.validateSession();
          if (!isValid) {
            get().logout();
          }
          return isValid;
        } catch (error) {
          console.error('Session validation error:', error);
          get().logout();
          return false;
        }
      }
    }),
    {
      name: 'employee-auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        employee: state.employee
      })
    }
  )
);

